// src/features/scope_module/components/InstructionsModal.js
import React, { useState } from 'react';
import {
  X, Play, Square, Edit3, Trash2, CheckCircle,
  LogOut, AlertTriangle, MousePointer, Layers
} from 'lucide-react';

const STORAGE_KEY = 'scope_hideInstructionsModal';

function InstructionsModal({ isOpen, onClose, onStart }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const handleStart = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    onStart();
    onClose();
  };

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    onClose();
  };

  return (
    <div className="instructions-modal-backdrop" onClick={handleClose}>
      <div className="instructions-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="instructions-modal-header">
          <div className="instructions-modal-icon">
            <Layers size={20} />
          </div>
          <h2 className="instructions-modal-title">SCOPE Examination Guide</h2>
          <button className="instructions-modal-close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="instructions-modal-body">
          <p className="instructions-intro">
            Welcome to the SCOPE fingerprint examination module. Please review the following instructions before beginning your assessment.
          </p>

          {/* Tool Instructions */}
          <div className="instructions-section">
            <h3 className="instructions-section-title">Examination Tools</h3>

            <div className="instructions-tool">
              <div className="instructions-tool-icon start">
                <Play size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Start</strong> - Begin tracking your examination. Your cursor movements and actions will be recorded.
              </div>
            </div>

            <div className="instructions-tool">
              <div className="instructions-tool-icon stop">
                <Square size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Stop</strong> - End tracking when you have finished examining the print pair. You can then review your work.
              </div>
            </div>

            <div className="instructions-tool">
              <div className="instructions-tool-icon add">
                <Edit3 size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Add Tool</strong> - Click on the image to mark minutiae points. Each click places a feature marker.
              </div>
            </div>

            <div className="instructions-tool">
              <div className="instructions-tool-icon delete">
                <Trash2 size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Delete Tool</strong> - Click near an existing minutiae point to remove it.
              </div>
            </div>

            <div className="instructions-tool">
              <div className="instructions-tool-icon conclusion">
                <CheckCircle size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Conclusion & Complete</strong> - After stopping, select your conclusion from the dropdown, then click Complete to finalize and submit your assessment.
              </div>
            </div>

            <div className="instructions-tool">
              <div className="instructions-tool-icon exit">
                <LogOut size={14} />
              </div>
              <div className="instructions-tool-content">
                <strong>Exit</strong> - Leave the examination and return to the dashboard.
              </div>
            </div>
          </div>

          {/* Workflow */}
          <div className="instructions-section">
            <h3 className="instructions-section-title">Examination Workflow</h3>
            <ol className="instructions-workflow">
              <li><span className="workflow-step">1</span> Click <strong>Start</strong> to begin tracking</li>
              <li><span className="workflow-step">2</span> Examine the fingerprint pair and mark minutiae using the Add/Delete tools</li>
              <li><span className="workflow-step">3</span> Click <strong>Stop</strong> when finished examining</li>
              <li><span className="workflow-step">4</span> Select your <strong>Conclusion</strong> from the dropdown</li>
              <li><span className="workflow-step">5</span> Click <strong>Complete</strong> to submit your assessment</li>
            </ol>
          </div>

          {/* Warning */}
          <div className="instructions-warning">
            <div className="instructions-warning-icon">
              <AlertTriangle size={18} />
            </div>
            <div className="instructions-warning-content">
              <strong>Important:</strong> If you exit the examination before completing, the session will be marked as finished with <strong>No Conclusion</strong>. You will not be able to return to this assessment.
            </div>
          </div>

          {/* Mouse tip */}
          <div className="instructions-tip">
            <MousePointer size={14} />
            <span>Tip: Use your mouse scroll wheel to zoom in/out on the fingerprint images for detailed examination.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="instructions-modal-footer">
          <label className="instructions-checkbox-label">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="instructions-checkbox"
            />
            <span>Don't show this message again</span>
          </label>

          <div className="instructions-modal-actions">
            <button className="instructions-btn cancel" onClick={handleClose}>
              Cancel
            </button>
            <button className="instructions-btn start" onClick={handleStart}>
              <Play size={14} />
              Begin Examination
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to check if modal should be shown
InstructionsModal.shouldShow = () => {
  return localStorage.getItem(STORAGE_KEY) !== 'true';
};

// Helper to reset the preference
InstructionsModal.resetPreference = () => {
  localStorage.removeItem(STORAGE_KEY);
};

export default InstructionsModal;
